import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useCreateScopeType } from '@/hooks/useScopeTypes';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import type { ScopeType } from '@/lib/schemas';

// Form validation schema - simplified for user input
const createScopeTypeFormSchema = z.object({
  display_name: z.string().min(1, 'Display name is required').max(200, 'Display name too long'),
  is_client_type: z.boolean().default(false),
});

type CreateScopeTypeFormData = z.infer<typeof createScopeTypeFormSchema>;

interface CreateScopeTypeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (scopeType: ScopeType) => void;
}

/**
 * Modal for creating a new scope type inline.
 * Used when adding a project scope and the desired type doesn't exist yet.
 */
export function CreateScopeTypeModal({
  open,
  onOpenChange,
  onCreated,
}: CreateScopeTypeModalProps) {
  const createScopeTypeMutation = useCreateScopeType();

  const { register, handleSubmit, formState: { errors }, reset, watch, setValue } = useForm<CreateScopeTypeFormData>({
    resolver: zodResolver(createScopeTypeFormSchema),
    defaultValues: {
      display_name: '',
      is_client_type: false,
    },
  });

  const isClientType = watch('is_client_type');

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);

  /**
   * Convert display name to slug format for the "name" field
   * Example: "Structural Engineer" → "structural_engineer"
   */
  const slugify = (text: string): string => {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '') // Remove special chars
      .replace(/[\s_-]+/g, '_') // Replace spaces/dashes with underscores
      .replace(/^-+|-+$/g, ''); // Remove leading/trailing dashes
  };

  const onSubmit = async (formData: CreateScopeTypeFormData) => {
    try {
      // Generate name from display_name
      const name = slugify(formData.display_name);

      // Create the scope type with all required fields
      const newScopeType = await createScopeTypeMutation.mutateAsync({
        name,
        display_name: formData.display_name,
        is_client_type: formData.is_client_type,
        display_order: 0, // Default to 0
      });

      // Notify parent component (with delay to allow React Query cache to propagate)
      // The optimistic update in useCreateScopeType updates the cache synchronously,
      // but React re-renders are async. We need to wait for the re-render to complete
      // before calling setValue in the parent, otherwise the Select component won't
      // have the new option available yet.
      setTimeout(() => {
        onCreated(newScopeType);
      }, 50); // Small delay to ensure React has re-rendered with updated cache

      // Close modal
      onOpenChange(false);

      // Reset form
      reset();
    } catch (error) {
      // Error is already handled by the mutation hook (toast shown)
      console.error('Failed to create scope type:', error);
    }
  };

  const handleCancel = () => {
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create Scope Type</DialogTitle>
          <DialogDescription>
            Create a new scope type that can be used when adding scopes to projects.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Display Name */}
          <div>
            <Label htmlFor="display_name">Display Name *</Label>
            <Input
              id="display_name"
              placeholder="e.g., Structural Engineer"
              {...register('display_name')}
            />
            {errors.display_name && (
              <p className="text-sm text-red-600 mt-1">{errors.display_name.message}</p>
            )}
          </div>

          {/* Is Client Type Checkbox */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="is_client_type"
              checked={isClientType}
              onCheckedChange={(checked) => setValue('is_client_type', checked === true)}
            />
            <Label
              htmlFor="is_client_type"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
            >
              Is Client Type
            </Label>
          </div>
          <p className="text-sm text-gray-500 mt-1 ml-6">
            Check this if this scope type represents the client organization on a project.
          </p>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={createScopeTypeMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createScopeTypeMutation.isPending}>
              {createScopeTypeMutation.isPending ? 'Creating...' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
